import React, { useEffect } from 'react'
import { useShipmentRateBlockStyle } from './shippment-rate-block.styles'
import {
  Backdrop,
  Box,
  Button,
  Fade,
  Grid,
  Modal,
  Typography,
} from '@material-ui/core'
import { useShipmentRateBlock } from './shippment-rate-block.hook'
import { ListRateForm } from './list-rate-form'
import { Loader } from '../../../components/loader'
import BorderColorIcon from '@material-ui/icons/BorderColor'
import { RateItem } from './rate-item'
import { useAppDialog } from '../../../hooks/use-app-dialog'
import { ApiError } from '../../../services/api/api-error-data'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { RateMessage } from '../../../store/checkout-page/types'
import { AppDialogVariants } from '../../../store/app-dialog/types'
import { GridItem } from '../../../shared/grid-item'
import { RatesUnavailableInline } from './rates-unavailable-inline'

export const ShipmentRateBlock: React.FC = (): React.ReactElement => {
  const classes = useShipmentRateBlockStyle()
  const {
    rates,
    ratesLoading,
    fullscreen,
    isOpen,
    saveAddressError,
    setIsOpen,
    addressLoading,
    getShipmentRateHandler,
    currentRate,
    setSelectedRateByArrayIndex,
    setLoadingRatesHandler,
    ratesError,
    rateMessages,
    resetStore,
  } = useShipmentRateBlock()

  const { setAppDialog, setIsShow } = useAppDialog()
  const { t } = useTranslation()
  const navigate = useNavigate()

  //Address Loading
  useEffect(() => {
    addressLoading && setLoadingRatesHandler()
    !addressLoading && !saveAddressError && getShipmentRateHandler()
  }, [addressLoading])

  //Rates Error
  useEffect(() => {
    if (ratesError) {
    } else if (saveAddressError) {
      setAppDialog({
        isShow: true,
        variant: AppDialogVariants.error,
        message: new ApiError(saveAddressError).getErrorMessage(t),
        title: t('accountShipping.saveErrorMessage'),
      })
    }
  }, [ratesError, saveAddressError])

  /** No rates from API but carrier returned explanatory messages */
  const isEmptyRate = (): boolean =>
    rates.length === 0 && rateMessages.length > 0

  //Empty Rates Array (Not Rates Error)
  useEffect(() => {
    if (isEmptyRate()) {
      setAppDialog({
        variant: AppDialogVariants.warning,
        isShow: true,
        message: <MessageBlockComponent rateMessages={rateMessages} />,
        title: t('checkout.ratesUnavailableTitle'),
        showCloseButton: true,
        actions: (
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setIsShow(false)
              navigate('/')
              resetStore()
            }}
          >
            {t('checkout.goToHome')}
          </Button>
        ),
      })
    }
  }, [rates, rateMessages])

  //Render
  return ratesLoading ? (
    <div className={classes.loader}>
      <Loader size={20} />
    </div>
  ) : (
    <>
      {/*Box*/}
      <Box
        justifyContent="flex-start"
        display="flex"
        className={classes.ratesWrapper}
      >
        {isEmptyRate() && <RatesUnavailableInline />}
        {/*Rates Not Empty*/}
        {!isEmptyRate() && rates.length > 0 && (
          <div className={classes.ratesHolder}>
            <Grid container item xs={12} spacing={2} justifyContent="flex-start">
              {/*Name*/}
              <Grid item xs={12} className={classes.nameLine}>
                {currentRate && <RateItem currentRate={currentRate} />}
                <div title={t('checkout.edit')} onClick={() => setIsOpen(true)}>
                  <BorderColorIcon className={classes.icon} />
                </div>
              </Grid>
            </Grid>
          </div>
        )}
      </Box>

      {/*Modal*/}
      <Modal
        data-testid="modal"
        data-modal-open={isOpen}
        closeAfterTransition
        open={isOpen}
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={isOpen}>
          <>
            <ListRateForm
              setSelectedRateByArrayIndex={setSelectedRateByArrayIndex}
              rates={rates}
              currentRate={currentRate}
              setIsOpen={setIsOpen}
              fullscreen={fullscreen}
            />
          </>
        </Fade>
      </Modal>
    </>
  )
}

//Type
type MessageBlockComponentProps = {
  rateMessages: RateMessage[]
}

//Message Block Component
const MessageBlockComponent: React.FC<MessageBlockComponentProps> = (
  props
): React.ReactElement => {
  const { rateMessages } = props

  if (!rateMessages.length) {
    return <></>
  }

  return (
    <Grid container spacing={2}>
      {rateMessages.map((item, key) => (
        <GridItem xs={12} key={key}>
          <Grid container spacing={1} alignItems="flex-start">
            <GridItem xs={12} sm={4} md={3}>
              <Typography variant="subtitle2" color="textPrimary" component="div">
                {item.source}:
              </Typography>
            </GridItem>
            <GridItem xs={12} sm={8} md={9}>
              <Typography variant="body2" color="textSecondary" component="div">
                {item.text}
              </Typography>
            </GridItem>
          </Grid>
        </GridItem>
      ))}
    </Grid>
  )
}
